import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useWorkspace } from './use-workspace';

// Types
import { SlackChannelType } from 'Shared/types/SlackChannel.type';
import { WorkspaceAiSettingType } from 'Shared/types/WorkspaceAiSetting.type';

// Selectors
import { getWorkspaceAiSettingsByWorkspaceId } from 'Shared/selectors/WorkspaceAiSettings.selectors';

// Actions
import { createWorkspaceAiSetting, updateWorkspaceAiSetting } from 'Shared/actions/WorksapceAiSettings.actions';
import { fetchAgendaAiSlackChannels } from 'Shared/actions/workspaces';

type Props = {
  fetchAvailableChannels?: boolean;
};

export const useWorkspaceAgendaAi = ({ fetchAvailableChannels = true }: Props = {}) => {
  const dispatch = useDispatch();

  const [isFetchingAvailableChannels, toggleIsFetchingAvailableChannels] = useToggle(false);
  const [availableChannels, setAvailableChannels] = useState<SlackChannelType[]>([]);

  const { workspace } = useWorkspace();
  const { id: workspaceId, slackAiOrganizationDataSource } = workspace;

  const workspaceAiSettings: WorkspaceAiSettingType[] = useSelector(state =>
    getWorkspaceAiSettingsByWorkspaceId(state, { workspaceId })
  );

  const isSlackAiActive = workspaceAiSettings.length > 0 ? workspaceAiSettings[0].active : false;
  const numOfActiveChannels = workspaceAiSettings.length > 0 ? workspaceAiSettings[0].channels.length : 0;
  const selectedChannelIds = workspaceAiSettings.length > 0 ? workspaceAiSettings[0].channels : [];
  const isDiscussionTopicPanelOpen =
    workspaceAiSettings.length > 0 ? workspaceAiSettings[0].discussionTopicPanelOpen : false;
  const isActionItemPanelOpen = workspaceAiSettings.length > 0 ? workspaceAiSettings[0].actionItemPanelOpen : false;
  const anyAiChatScanRequests = workspaceAiSettings.length > 0 ? workspaceAiSettings[0].anyAiChatScanRequests : false;

  const selectedChannels =
    workspaceAiSettings.length > 0
      ? availableChannels.filter(channel => workspaceAiSettings[0].channels.includes(channel.id))
      : [];

  const slackAiOrganizationDataSourceId = slackAiOrganizationDataSource?.data?.id;

  const handleCreateWorkspaceAiSetting = data => {
    return dispatch(
      createWorkspaceAiSetting({
        workspaceId,
        data: { workspace_ai_settings: { ...data, organization_data_source_id: slackAiOrganizationDataSourceId } },
      })
    ).then(response => {
      return response;
    });
  };

  const handleUpdateWorkspaceAiSetting = data => {
    return dispatch(
      updateWorkspaceAiSetting({
        workspaceId,
        workspaceAiSettingSlug: workspaceAiSettings[0].slug,
        data: { workspace_ai_settings: { ...data } },
      })
    ).then(response => {
      return response;
    });
  };

  const handleCreateOrUpdateWorkspaceAiSetting = data => {
    if (workspaceAiSettings.length > 0) {
      return handleUpdateWorkspaceAiSetting(data).then(response => response);
    } else {
      return handleCreateWorkspaceAiSetting(data).then(response => response);
    }
  };

  useEffect(() => {
    // only fetch channels if it is connected to slack
    if (fetchAvailableChannels && slackAiOrganizationDataSourceId) {
      toggleIsFetchingAvailableChannels();
      fetchAgendaAiSlackChannels({ workspaceId })
        .then(response => {
          if (response.status === 200) {
            setAvailableChannels(response.data);
          }
        })
        .finally(() => toggleIsFetchingAvailableChannels());
    }
  }, [fetchAvailableChannels]);

  return {
    isFetching: isFetchingAvailableChannels,
    availableSlackchannels: availableChannels,
    slackAiOrganizationDataSourceId,
    selectedChannels,
    anyAiChatScanRequests,
    isSlackAiActive,
    numOfActiveChannels,
    selectedChannelIds,
    isDiscussionTopicPanelOpen,
    isActionItemPanelOpen,
    handleCreateOrUpdateWorkspaceAiSetting,
    handleUpdateWorkspaceAiSetting,
  };
};
